import React from 'react'
import { loadWhitelabelSettings, WhitelabelLoadError } from '../../_utils/whitelabeling'
import { classifyError, type ErrorKind } from '../../core/error-kind'
import { useBoot } from '../../core/boot/boot-provider'
import { WhitelabelConfigModel } from '../../models/whitelabel-config/whitelabel-config.model'
import { streamApplicationError } from '../../_utils/airbrake'
import { useService } from '@pushly/aqe/lib/hooks'
import { AppState } from '../../stores/app'

type PlatformIdentity =
    | {
          loaded: false
      }
    | {
          loaded: true
          whiteLabelSettings: WhitelabelConfigModel
      }

const initialPlatformIdentity: PlatformIdentity = { loaded: false }
export const PlatformIdentityContext = React.createContext<PlatformIdentity>(initialPlatformIdentity)
let platformIdentitySnapshot: Readonly<PlatformIdentity> = { ...initialPlatformIdentity }
export const getPlatformIdentitySnapshot = (): Readonly<PlatformIdentity> => platformIdentitySnapshot

function whiteLabelMissingRequiredProperties(label: WhitelabelConfigModel): boolean {
    return (
        !label.getAuth0AudienceName() ||
        !label.getAuth0TenantId() ||
        !label.getAuth0PlatformClientId() ||
        !label.getAuth0HostedLoginDomain()
    )
}

class PlatformIdentityException extends Error {
    public entity?: WhitelabelConfigModel

    public constructor(label?: WhitelabelConfigModel) {
        super('Platform Identity could not be determined.')
        Object.setPrototypeOf(this, PlatformIdentityException.prototype)

        this.entity = label
    }
}

/**
 * Loads tier 0 when boot is in the `identity` phase and reports the outcome to the boot machine.
 * It renders its children in every phase: the boot gate below it decides what the user sees, and a
 * gate cannot open if the loader it waits for is hidden behind it.
 */
export const PlatformIdentityProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
    const appState = useService(AppState)
    const { phase, send } = useBoot()
    const [identity, _setIdentity] = React.useState<PlatformIdentity>(initialPlatformIdentity)
    const loading = phase.phase === 'identity'

    const setIdentity = (value: PlatformIdentity) => {
        const snapshot = { ...value }
        Object.freeze(snapshot)

        platformIdentitySnapshot = snapshot
        _setIdentity(value)
    }

    React.useEffect(() => {
        if (!loading) {
            return
        }

        let superseded = false

        const loadIdentity = async () => {
            try {
                const whiteLabelSettings = await loadWhitelabelSettings()
                if (whiteLabelMissingRequiredProperties(whiteLabelSettings)) {
                    throw new PlatformIdentityException(whiteLabelSettings)
                }
                if (superseded) {
                    return
                }

                setIdentity({
                    loaded: true,
                    whiteLabelSettings,
                })

                appState.whiteLabelSettings = whiteLabelSettings
                send({ type: 'identityLoaded' })
            } catch (error) {
                if (superseded) {
                    return
                }

                const kind: ErrorKind =
                    error instanceof PlatformIdentityException
                        ? 'misconfigured'
                        : error instanceof WhitelabelLoadError
                        ? error.kind
                        : classifyError(error)

                if (error instanceof PlatformIdentityException) {
                    streamApplicationError(error, {
                        detailedName: 'loading_whitelabel_settings_error',
                        platformIdentity: error.entity,
                        tier: 'identity',
                        kind,
                    })
                } else {
                    streamApplicationError(error, { tier: 'identity', kind })
                }

                send({ type: 'identityFailed', kind, error })
            }
        }

        loadIdentity()

        return () => {
            superseded = true
        }
    }, [loading])

    return <PlatformIdentityContext.Provider value={identity}>{children}</PlatformIdentityContext.Provider>
}
